import { useFormik, useFormikContext } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import DataGrid, { SelectColumn, SortColumn } from 'react-data-grid';
import {
    Employee,
    SearchFilter,
    SeoOrders,
    SubTaskReAssignInput,
    usePaginatedSubTaskListQuery,
    useViewOrderUpdateMutation,
} from '../../api/generated';
import { PaginationFooter } from '../main/PaginationFooter';
import { SearchBar } from '../main/SearchBar';

interface orderDetailsTableRows {
    id: string;
    clientSiteURL: string;
    keyword: string;
    liveLinks: string;
    articleLinks: string;
    requiredDaDr: string;
    EmployeeAssigned: ReactElement;
    dateOfAssignment: string;
}

interface assignEmployee {
    seoOrderID: number;
    seoEmployeeID: number;
}

interface InitialValues {
    pageNum: number;
    paginationRowCount: number;
    totalPages: number;
    totalRows: number;
    sortBy: string;
    sortType: string;
    edited: boolean;
    search: SearchFilter;
    subTaskAssignment: [assignEmployee];
}

export default function TablesOrder(props: {
    order: SeoOrders;
    employeesAssignable: Employee[];
}) {
    const formikContext = useFormikContext<InitialValues>();
    const [updateOrder] = useViewOrderUpdateMutation();

    const formik = useFormik({
        initialValues: {
            subTaskAssignment: [],
            liveLinks: [],
            articleLinks: [],
            clientSiteURL: [],
            keyword: [],
            edited: false,
        },
        onSubmit: () => {
            updateOrder({
                variables: {
                    input: {
                        subTask: formik.values.subTaskAssignment,
                        liveLinks: formik.values.liveLinks,
                        articleLinks: formik.values.articleLinks,
                        clientSiteUrl: formik.values.clientSiteURL,
                        keyword: formik.values.keyword,
                    },
                },
            });
            formik.setFieldValue('subTaskAssignment', []);
            formik.setFieldValue('articleLinks', []);
            formik.setFieldValue('liveLinks', []);
            formik.setFieldValue('clientSiteURL', []);
            formik.setFieldValue('keyword', []);
            // subTaskRefetch();
        },
    });

    const columns = [
        SelectColumn,
        { key: 'id', name: 'Sr.No' },
        {
            key: 'clientSiteURL',
            name: 'Client Site URL',
            sortable: true,
        },
        {
            key: 'keyword',
            name: 'Client Keyword',
            sortable: true,
        },

        {
            key: 'liveLinks',
            name: 'Live Links',
        },
        {
            key: 'articleLinks',
            name: 'Article Links',
        },
        {
            key: 'requiredDaDr',
            name: 'DA/DR Required',
            sortable: true,
        },

        {
            key: 'EmployeeAssigned',
            name: 'Assigned To Employee ',
            sortable: true,
        },
        {
            key: 'dateOfAssignment',
            name: 'Date of Assignment',
            sortable: true,
        },
    ];

    const { data: subTasks } = usePaginatedSubTaskListQuery({
        variables: {
            input: {
                take: Number(formikContext.values.paginationRowCount),
                skip:
                    Number(formikContext.values.pageNum) *
                    Number(formikContext.values.paginationRowCount),
                orderID: props.order && props.order.order?.id,
                search: formikContext.values.search,
            },
        },
    });

    useEffect(() => {
        formikContext.setFieldValue('pageNum', 0);
    }, [formikContext.values.paginationRowCount]);

    useEffect(() => {
        formikContext.setFieldValue(
            'totalPages',
            subTasks?.paginatedSubTaskList?.totalPages
        );
    }, [subTasks?.paginatedSubTaskList?.totalPages]);

    useEffect(() => {
        formikContext.setFieldValue(
            'totalRows',
            subTasks?.paginatedSubTaskList?.totalRows
        );
    }, [subTasks?.paginatedSubTaskList?.totalRows]);

    const rows: orderDetailsTableRows[] = [];
    subTasks?.paginatedSubTaskList?.seoOrderList.map((seoOrder) => {
        const cmsOrder = seoOrder?.CmsOrders && seoOrder?.CmsOrders[0];
        return rows.push({
            id: seoOrder?.id as string,
            clientSiteURL: `${
                seoOrder?.ClientSiteUrl &&
                seoOrder?.ClientSiteUrl[0]?.clientSiteURL
            } ${''} `,
            keyword: `${
                seoOrder?.ClientSiteUrl && seoOrder?.ClientSiteUrl[0]?.keyword
            } ${''} `,
            liveLinks: ` ${
                seoOrder?.LiveLinks && seoOrder?.LiveLinks[0]?.liveLinks
            } ${''} `,
            articleLinks: ` ${
                cmsOrder?.Articles && cmsOrder?.Articles[0]?.articleLink
            } ${''} `,
            requiredDaDr: `${
                seoOrder?.ClientSiteUrl &&
                seoOrder?.ClientSiteUrl[0]?.requiredDA
            } / ${
                seoOrder?.ClientSiteUrl && seoOrder.ClientSiteUrl[0]?.requiredDR
            }`,
            EmployeeAssigned: (
                <select
                    className="w-full text-center bg-transparent p-2 m-0"
                    onChange={(event) => {
                        formik.setFieldValue('edited', false);
                        formik.setFieldValue('subTaskAssignment', [
                            {
                                id: seoOrder?.id,
                                seoEmpID: Number(event.target.value),
                            },
                            ...formik.values.subTaskAssignment.filter(
                                (subTask: SubTaskReAssignInput) =>
                                    subTask.id === seoOrder?.id
                            ),
                        ]);
                    }}
                >
                    <option value={seoOrder?.seoEmp?.id as number}>
                        {seoOrder?.seoEmp?.firstName?.concat(
                            ` ${seoOrder?.seoEmp?.lastName}`
                        )}
                    </option>
                    {props?.employeesAssignable?.map(
                        (employee) =>
                            employee && (
                                <option
                                    key={employee.id}
                                    value={employee.id?.toString()}
                                >
                                    {employee.firstName?.concat(
                                        ` ${employee?.lastName}` as string
                                    )}
                                </option>
                            )
                    )}
                </select>
            ),
            dateOfAssignment: seoOrder?.dateOfAssignment?.substring(0, 10),
        });
    });

    const [sortColumns, setSortColumns] = useState<SortColumn[]>([]);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(
        () => new Set()
    );
    return (
        <div className="text-center bg-transparent">
            <div className="text-left p-5 space-x-6 static">
                <div className=" text-centerp-5 inline-block align-middle text-zedex-button-blue lg:mr-48">
                    Tasks: {props.order && props.order.order?.ArticleCounts}
                </div>
                <SearchBar placeholder="Search for SubTask" />
            </div>
            {formik.values.edited ? (
                <div className="w-[30rem] grid grid-cols-2 mb-4 ml-5 p-3 float-left">
                    <button
                        type="button"
                        onClick={() => {
                            formik.setFieldValue('edited', false);
                            formik.handleSubmit();
                        }}
                        className="bg-zedex-button-blue text-white create-order align-middle p-1.5 w-48 hover:bg-zedex-button-blue-hover"
                    >
                        <div className="space-x-2 align-middle">
                            <span className="">Save</span>
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            formik.setFieldValue('edited', false);
                            formik.resetForm();
                            // subTaskRefetch();
                            window.location.reload();
                        }}
                        className="bg-zedex-button-blue text-white create-order align-middle p-1.5 w-48 hover:bg-zedex-button-blue-hover"
                    >
                        <div className="space-x-2 align-middle">
                            <span className="">Reset</span>
                        </div>
                    </button>
                </div>
            ) : null}
            <div>
                {rows.length === 0 ? (
                    <div className="rounded-lg p-5 bg-blue-100 text-blue-500 text-center">
                        No Data To be Displayed
                    </div>
                ) : (
                    <DataGrid
                        className="rdg-light fill-grid w-full"
                        columns={columns}
                        rows={rows}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={setSelectedRows}
                        sortColumns={sortColumns}
                        onSortColumnsChange={setSortColumns}
                        defaultColumnOptions={{
                            sortable: true,
                            minWidth: 250,
                            resizable: true,
                        }}
                        rowHeight={60}
                    />
                )}
            </div>
            <div>
                <PaginationFooter />
            </div>
        </div>
    );
}
